import { isAfter, isBefore } from 'date-fns/fp';
import { getIn } from 'formik';
import padStart from 'lodash/padStart';
import { inRange, isEqual } from 'lodash';
import { kebabCase } from 'change-case';

import { MONTH } from 'core/constants';
import { InvestmentOption as InvestmentOptionPaymentMethod } from 'core/api/client/InvestmentApi';
import { InvestmentOption } from 'jss-components/t17-investments/EditInvestmentOptions/EditInvestmentOptions.types';
import { TInvestmentOption } from 'jss-components/t19-withdrawals/subcomponents/hooks/useGetInvestmentOptions.hooks';

import CONTENT from './_formBuilder.content';
import { AUSTRALIA_COUNTRY_CODE } from './components/related/AddressFields';

const getDate = (dateStr: string) => {
	const [day, month, year] = dateStr.split('/');

	return new Date(Number(year), Number(month) - 1, Number(day) + 1);
};

const weightingSequence = [1, 4, 3, 7, 5, 8, 6, 9, 10];

function verifyWeighting(fieldValue: string) {
	// Prepends 0 to eight digit TFN - note fieldValue is already type string
	const paddedInput = padStart(fieldValue, 9, '0');

	const charArr = paddedInput.split('');
	let weighting = 0;

	// Multiplies weightItem index item with same index item of 9 digit TFN
	weightingSequence.forEach((weightingItem, idx) => {
		weighting = weighting + weightingItem * parseInt(charArr[idx], 10);
	});

	// Check if total weighting is multiple of 11
	const remainder = weighting % 11;

	return remainder === 0;
}

export interface IProps {
	fieldValue: any;
	message: string;
	formValues: {
		[key: string]: any;
	};
	options?: any;
}

export interface IMaxWithdrawalAmountForInvestmentOptionsProps {
	withdrawalAmountField: string;
	investmentOptions: TInvestmentOption[];
}

export type ValidatorFn = (args: Partial<IProps>) => string | undefined;

export default {
	'required': ({ fieldValue, message }: IProps) => {
		// override the empty string with default error message
		message = message || CONTENT.VALIDATORS.DEFAULT.REQUIRED;

		// ignore the falsy value if the value is boolean
		if (!fieldValue && typeof fieldValue !== 'boolean') {
			return message;
		}

		// check for empty string
		if (
			(typeof fieldValue === 'string' || fieldValue instanceof String) &&
			!fieldValue.trim()
		) {
			return message;
		}

		// check for empty array
		if (Array.isArray(fieldValue) && !fieldValue.length) {
			return message;
		}

		return undefined;
	},
	'minLength': ({ fieldValue, message, options }: IProps) => {
		message = message || CONTENT.VALIDATORS.DEFAULT.MIN_LENGTH;

		if (!fieldValue) {
			return undefined;
		}

		if (fieldValue.length < options.min) {
			return message;
		}

		return undefined;
	},

	'stringRegex': ({ fieldValue, message, options }: IProps) => {
		message = message || CONTENT.VALIDATORS.DEFAULT.VALID_STRING;
		const { regExPattern } = options;

		if (!regExPattern || String(regExPattern).length === 0) {
			return undefined;
		}

		const regex = new RegExp(regExPattern);

		if (!fieldValue) {
			return undefined;
		}

		if (!regex.test(fieldValue)) {
			return message;
		}

		return undefined;
	},

	'maxLength': ({ fieldValue, message, options }: IProps) => {
		message = message || CONTENT.VALIDATORS.DEFAULT.MAX_LENGTH;

		if (!fieldValue) {
			return undefined;
		}

		if (fieldValue.length > options.max) {
			return message;
		}

		return undefined;
	},

	'email': ({ fieldValue, message }: IProps) => {
		message = message || CONTENT.VALIDATORS.DEFAULT.EMAIL;
		const regex = new RegExp(
			/^(?!.{121})([a-zA-Z0-9_\-\.]+)@([a-zA-Z0-9_\-\.]+)\.([a-zA-Z0-9_\-\.]+)$/,
		);

		if (!fieldValue) {
			return undefined;
		}

		if (!regex.test(fieldValue)) {
			return message;
		}

		return undefined;
	},

	'emailOrMobile': ({ fieldValue, message }: IProps) => {
		message = message || CONTENT.VALIDATORS.DEFAULT.EMAIL_OR_MOBILE;
		const emailRegex = new RegExp(
			/^(?!.{121})([a-zA-Z0-9_\-\.]+)@([a-zA-Z0-9_\-\.]+)\.([a-zA-Z]{2,5})$/,
		);

		// https://stackoverflow.com/questions/39990179/regex-for-australian-phone-number-validation
		// /^(?:\+?(61))? ?(?:\((?=.*\)))?(0?[2-57-8])\)? ?(\d\d(?:[- ](?=\d{3})|(?!\d\d[- ]?\d[- ]))\d\d[- ]?\d[- ]?\d{3})$/,
		const intlMobileRegex = new RegExp(
			/^(([+][(]?[0-9]{1,3}[)]?)|([(]?[0-9]{4}[)]?))\s*[)]?[-\s\.]?[(]?[0-9]{1,3}[)]?([-\s\.]?[0-9]{3})([-\s\.]?[0-9]{3,4})$/,
		);

		const intlAuMobileRegex = new RegExp(
			/^(?:\+?(61))? ?(?:\((?=.*\)))?(0?[2-57-8])\)? ?(\d\d(?:[- ](?=\d{3})|(?!\d\d[- ]?\d[- ]))\d\d[- ]?\d[- ]?\d{3})$/,
		);

		if (!fieldValue) {
			return undefined;
		}

		if (
			!emailRegex.test(fieldValue) &&
			(intlMobileRegex.test(fieldValue) || intlAuMobileRegex.test(fieldValue))
		) {
			return undefined;
		}

		if (
			(!intlMobileRegex.test(fieldValue) ||
				!intlAuMobileRegex.test(fieldValue)) &&
			emailRegex.test(fieldValue)
		) {
			return undefined;
		}

		if (
			(!intlMobileRegex.test(fieldValue) ||
				!intlAuMobileRegex.test(fieldValue)) &&
			!emailRegex.test(fieldValue)
		) {
			return message;
		}

		return undefined;
	},

	'member-number': ({ fieldValue, message }: IProps) => {
		message = message || CONTENT.VALIDATORS.DEFAULT.MEMBER_NUMBER;
		const regex = new RegExp(/^[0-9]{1,9}$/);

		if (!fieldValue) {
			return undefined;
		}
		// remove dash or space as per FF18 from GL04
		const cleanedFieldValue = String(fieldValue).replace(/\s|-/g, '');

		if (!regex.test(cleanedFieldValue)) {
			return message;
		}

		return undefined;
	},

	'registrationMemberNumber': ({ fieldValue, message }: IProps) => {
		message = message || CONTENT.VALIDATORS.DEFAULT.REGISTRATION_MEMBER_NUMBER;
		const regex = new RegExp(/^[0-9]{1,20}$/);

		if (!fieldValue) {
			return undefined;
		}
		// remove dash or space as per FF18 from GL04
		const cleanedFieldValue = String(fieldValue).replace(/\s|-/g, '');

		if (!regex.test(cleanedFieldValue)) {
			return message;
		}

		return undefined;
	},

	'usernameLength': ({ fieldValue, message }: IProps) => {
		message = message || CONTENT.VALIDATORS.DEFAULT.USERNAME;
		const regex = new RegExp(/^(.{1,100})$/);

		if (!fieldValue) {
			return undefined;
		}

		if (!regex.test(fieldValue)) {
			return message;
		}

		return undefined;
	},

	'pwdValidator': ({ fieldValue, message }: IProps) => {
		// TODO we probablhy need to revise this below logic for AusSuper logic
		const errMsg = message || CONTENT.VALIDATORS.DEFAULT.REQUIRED_PWD_MESSAGE;
		// Ref: https://www.thepolyglotdeveloper.com/2015/05/use-regex-to-test-password-strength-in-javascript/
		// Modified according to requirements
		const regex = new RegExp('^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.{8,})');
		const invalidRegex = !regex.test(fieldValue);
		const isValid = !invalidRegex;
		if (!fieldValue) {
			return undefined;
		}
		if (!isValid) {
			return errMsg;
		}

		return undefined;
	},

	'emailFormatPwdValidator': ({ fieldValue, message }: IProps) => {
		const errMsg =
			message || CONTENT.VALIDATORS.DEFAULT.DISALLOW_EMAIL_FORMAT_PASSWORD;
		const regex = new RegExp(
			'^(([^<>()[\\]\\.,;:\\s@"]+(\\.[^<>()[\\]\\.,;:\\s@"]+)*)|(".+"))@((\\[[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}\\])|(([a-zA-Z\\-0-9]+\\.)+[a-zA-Z]{2,}))',
		);
		const isValid = !regex.test(fieldValue);
		if (!fieldValue) {
			return undefined;
		}
		if (!isValid) {
			return errMsg;
		}

		return undefined;
	},
	//This will check if all of the fields from date is empty even if we click out to specific field
	'required-date': ({ formValues, message, options }: IProps) => {
		// override the empty string with default error message
		message = message || CONTENT.VALIDATORS.DEFAULT.REQUIRED;

		const {
			targetFields: { day, month, year },
		} = options;
		const monthFieldValue = formValues[month];
		const dayValue = formValues[day];
		const yearValue = formValues[year];

		// find the month in number
		const monthNumber =
			monthFieldValue > 0
				? // use the field value if it is a number or a string representing a number such as '12'
				  monthFieldValue
				: MONTH.findIndex(monthName =>
						RegExp(monthName, 'i').test(monthFieldValue),
				  ) + 1;

		if (!dayValue || !yearValue || monthNumber <= 0) {
			return message;
		}
	},

	'date': ({ fieldValue, formValues, options, message }: IProps) => {
		message = message || CONTENT.VALIDATORS.DEFAULT.DATE;
		// Ref: https://stackoverflow.com/a/15504877
		// Edited according to comments there to enforce YYYY instead of YY
		const regex = new RegExp(
			/^(?:(?:31(\/|-|\.)(?:0?[13578]|1[02]))\1|(?:(?:29|30)(\/|-|\.)(?:0?[1,3-9]|1[0-2])\2))(?:(?:1[6-9]|[2-9]\d)\d{2})$|^(?:29(\/|-|\.)0?2\3(?:(?:(?:1[6-9]|[2-9]\d)(?:0[48]|[2468][048]|[13579][26])|(?:(?:16|[2468][048]|[3579][26])00))))$|^(?:0?[1-9]|1\d|2[0-8])(\/|-|\.)(?:(?:0?[1-9])|(?:1[0-2]))\4(?:(?:1[6-9]|[2-9]\d)\d{2})$/,
		);

		if (!fieldValue) {
			return undefined;
		}

		if (options) {
			// when options provided, assume the date is entered through multiple fields
			const {
				targetFields: { day, month, year },
			} = options;
			const monthFieldValue = formValues[month];
			const dayValue = formValues[day];
			const yearValue = formValues[year];
			// find the month in number
			const monthNumber =
				monthFieldValue > 0
					? // use the field value if it is a number or a string representing a number such as '12'
					  monthFieldValue
					: MONTH.findIndex(monthName =>
							RegExp(monthName, 'i').test(monthFieldValue),
					  ) + 1;
			// only check when all three fields are not empty
			if (!dayValue || !yearValue || monthNumber <= 0) {
				return undefined;
			}

			const combinedDate = `${dayValue}-${monthNumber}-${yearValue}`;

			if (!regex.test(combinedDate)) {
				return message;
			}

			const dateValues = new Date(yearValue, monthFieldValue - 1, dayValue);

			const today = new Date();
			const dateToday = new Date(
				today.getFullYear(),
				today.getMonth(),
				today.getDate(),
			);

			if (dateValues.getTime() > dateToday.getTime()) {
				return CONTENT.VALIDATORS.DEFAULT.FUTURE_DOB;
			}

			return undefined;
		}

		if (!regex.test(fieldValue)) {
			return message;
		}

		return undefined;
	},

	'date-before': ({ fieldValue, formValues, message, options }: IProps) => {
		message = message || CONTENT.VALIDATORS.DEFAULT.DATE_BEFORE;

		const targetField = options.targetField;
		const targetValue = formValues[targetField];

		if (!fieldValue) {
			return undefined;
		}

		if (isBefore(getDate(fieldValue), getDate(targetValue))) {
			return message;
		}

		return undefined;
	},

	'date-after': ({ fieldValue, formValues, message, options }: IProps) => {
		message = message || CONTENT.VALIDATORS.DEFAULT.DATE_AFTER;

		const targetField = options.targetField;
		const targetValue = formValues[targetField];

		if (isAfter(getDate(fieldValue), getDate(targetValue))) {
			return message;
		}

		return undefined;
	},

	'same-value': ({ fieldValue, formValues, message, options }: IProps) => {
		const msgType = options.messageType;
		if (!!message) {
			message = message;
		} else if (!!msgType && msgType === 'confirm-password') {
			message = CONTENT.VALIDATORS.DEFAULT.SAME_VALUE_CONFIRM_PASSWORD;
		} else {
			message = CONTENT.VALIDATORS.DEFAULT.SAME_VALUE;
		}

		const targetField = options.targetField;
		const targetValue = formValues[targetField];

		if (!fieldValue) {
			return undefined;
		}

		if (fieldValue !== targetValue) {
			return message;
		}

		return undefined;
	},

	'phone': ({ fieldValue, message }: IProps) => {
		message = message || CONTENT.VALIDATORS.DEFAULT.PHONE;

		const regex = new RegExp(/^(?!.*  )[0-9-+() ]{0,40}$/);
		const isValid = regex.test(fieldValue);

		if (!fieldValue) {
			return undefined;
		}

		if (!isValid) {
			return message;
		}

		return undefined;
	},

	'mobile-phone': ({ fieldValue, message }: IProps) => {
		message = message || CONTENT.VALIDATORS.DEFAULT.PHONE;

		const regex = new RegExp(/^(?!.*  )[0-9+ ]{0,20}$/);
		const isValid = regex.test(fieldValue);

		if (!fieldValue) {
			return undefined;
		}

		if (!isValid) {
			return message;
		}

		return undefined;
	},

	'street-address': ({ fieldValue, message }: IProps) => {
		message = message || CONTENT.VALIDATORS.DEFAULT.PHONE;

		const regex = new RegExp(/^[A-Za-z0-9'\.\-\s\,\/]+$/);
		const isValid = regex.test(fieldValue);

		if (!fieldValue) {
			return undefined;
		}

		if (!isValid) {
			return message;
		}

		return undefined;
	},

	'suburb-address': ({ fieldValue, message }: IProps) => {
		message = message || CONTENT.VALIDATORS.DEFAULT.SPECIAL_CHARACTERS;

		const regex = new RegExp(/^[A-Za-z0-9\-\s\/]+$/);
		const isValid = regex.test(fieldValue);

		if (!fieldValue) {
			return undefined;
		}

		if (!isValid) {
			return message;
		}

		return undefined;
	},

	'tfnFormat': ({ fieldValue, message }: IProps) => {
		const errMsg = message || CONTENT.VALIDATORS.DEFAULT.TFN_FORMAT;
		fieldValue = fieldValue.trim().replace(/\s/g, '');
		const regex = new RegExp(/^[0-9]{8,9}$/);
		const invalidRegex = !regex.test(fieldValue);
		const isValid = !invalidRegex && verifyWeighting(fieldValue);

		if (!fieldValue) {
			return undefined;
		}

		if (!isValid) {
			return errMsg;
		}

		return undefined;
	},

	'value': ({ fieldValue, message, options }: IProps) => {
		message = message || CONTENT.VALIDATORS.DEFAULT.VALUE;

		if (!fieldValue) {
			return undefined;
		}

		try {
			fieldValue = parseFloat(fieldValue);
		} catch (err) {
			return undefined;
		}

		switch (options.type) {
			case 'lt':
				return fieldValue < options.value ? undefined : message;
			case 'lte':
				return fieldValue <= options.value ? undefined : message;
			case 'gt':
				return fieldValue > options.value ? undefined : message;
			case 'gte':
				return fieldValue >= options.value ? undefined : message;
			case 'eq':
			default:
				return fieldValue === options.value ? undefined : message;
		}
	},

	'name': ({ fieldValue, message }: IProps) => {
		message = message || CONTENT.VALIDATORS.DEFAULT.NAME;
		// NOTE: refer to https://australiansuper.atlassian.net/wiki/spaces/MemberPortalV5/pages/46406580/GL04+-+Common+CMS+Definitions+and+Form+Fields
		const regex = new RegExp(/^(?:(?!_)[\w\d](?:\s|-|'|’)?)+$/);

		if (!fieldValue) {
			return undefined;
		}

		return regex.test(fieldValue) ? undefined : message;
	},

	'fundName': ({ fieldValue, message }: IProps) => {
		message = message || CONTENT.VALIDATORS.DEFAULT.NAME;
		// NOTE: refer to https://australiansuper.atlassian.net/wiki/spaces/MemberPortalV5/pages/46406580/GL04+-+Common+CMS+Definitions+and+Form+Fields
		const regex = new RegExp(/^[a-zA-Z0-9&' .-]+$/);

		if (!fieldValue) {
			return undefined;
		}

		return regex.test(fieldValue) ? undefined : message;
	},

	'spinUsiNumber': ({ fieldValue, message }: IProps) => {
		message = message || CONTENT.VALIDATORS.DEFAULT.SPINUSINUMBER;
		// two capital characters followed by four numbers followed by two capital characters
		const regex = new RegExp(/^[A-Z]{3}\d{4}[A-Z]{2}/g);
		if (!fieldValue) {
			return undefined;
		}

		return regex.test(fieldValue) ? undefined : message;
	},

	'bsb': ({ fieldValue, message }: IProps) => {
		message = message || CONTENT.VALIDATORS.DEFAULT.BSB;
		// six digit number can be separate with space or dash
		const regex = new RegExp(/^\d{3}(-?|\s?)\d{3}$/g);

		if (!fieldValue) {
			return undefined;
		}

		return regex.test(fieldValue) ? undefined : message;
	},

	'bsb-api': ({ fieldValue, message, options }: IProps) => {
		message = message || CONTENT.VALIDATORS.DEFAULT.BSB;

		if (!fieldValue) {
			return undefined;
		}

		return options?.bsbMessage === true ? message : undefined;
	},

	'withdrawalAmountMatch': ({ formValues, message, options }: IProps) => {
		const { prefix, withdrawAmount } = options;
		const totalInput = Object.keys(formValues).reduce((runTotal, current) => {
			if (current.includes(prefix)) {
				const numVal = parseFloat(formValues[current]);

				return runTotal + (!isNaN(numVal) ? numVal : 0);
			}

			return runTotal;
		}, 0);

		if (totalInput !== parseFloat(withdrawAmount)) {
			return message;
		}

		return undefined;
	},

	/*** Validation to display minimum value error with customizable message and threshold */
	'minimumValue': ({ fieldValue, message, options }: IProps) => {
		message = message || CONTENT.VALIDATORS.WITHDRAWALS.MINIMUM_VALUE;
		const minThreshold = options?.minThreshold || 1000;
		const amountToTransfer = parseFloat(fieldValue);

		if (!isNaN(minThreshold) && !isNaN(amountToTransfer)) {
			return amountToTransfer >= minThreshold ? undefined : message;
		}

		return undefined;
	},

	/*** Validation to make sure that the withdrawal amount should not be greater than the account's balance */
	'minWithdrawalAmount': ({ fieldValue, message, options }: IProps) => {
		message = message || CONTENT.VALIDATORS.WITHDRAWALS.MINIMUM_BALANCE;
		const minimumBalance = options?.minimumBalance
			? parseFloat(options?.minimumBalance)
			: 1000;
		const availableBalance = parseFloat(options?.availableBalanceField);
		const amountToTransfer = parseFloat(fieldValue);

		if (!isNaN(availableBalance) && !isNaN(amountToTransfer)) {
			return availableBalance - amountToTransfer >= minimumBalance
				? undefined
				: message;
		}

		return undefined;
	},

	/*** Validation to make sure that the withdrawal amount should not be greater than the maximum amount value */
	'maxWithdrawalAmount': ({ fieldValue, message, options }: IProps) => {
		const maximumAmount = options?.maximumAmount
			? parseFloat(options?.maximumAmount)
			: 10000;
		const amountToTransfer = parseFloat(fieldValue);

		if (!isNaN(amountToTransfer)) {
			return amountToTransfer > maximumAmount ? message : undefined;
		}

		return undefined;
	},

	/**
	 * This validates the pension:withdrawal with more than 1 investment options
	 * The required field validation will only be triggered,
	 * if the user leaves the investment amount field all empty or with the value of zero
	 */
	'requiredInvestmentOptions': ({ formValues, options, message }: IProps) => {
		message = message || CONTENT.VALIDATORS.DEFAULT.EMPTY;
		if (formValues) {
			const totalInvestmentAmountToWithdraw = Object.keys(formValues).reduce(
				(runTotal, current) => {
					if (current.includes(options.prefix)) {
						const numVal = parseFloat(formValues[current]);

						return runTotal + (!isNaN(numVal) ? numVal : 0);
					}

					return Number(runTotal.toString().match(/^\d+(?:\.\d{0,2})?/));
				},
				0,
			);
			if (totalInvestmentAmountToWithdraw <= 0) {
				return message;
			}
		}

		return undefined;
	},

	'postcode': ({ fieldValue, formValues, message, options }: IProps) => {
		message =
			options.postcodeErrorMessages.au ?? CONTENT.VALIDATORS.DEFAULT.POSTCODE;
		const targetValue = getIn(formValues, options.targetField);

		if (!fieldValue || targetValue !== AUSTRALIA_COUNTRY_CODE) {
			return undefined;
		}

		const regex =
			formValues[options.addressPathPrefix].state === 'NT'
				? // if NT, allow 0 start (old regex)
				  new RegExp(
						/^((0[289][0-9]{2})|([1345689][0-9]{3})|(2[0-8][0-9]{2})|(290[0-9])|(291[0-4])|(7[0-4][0-9]{2})|(7[8-9][0-9]{2}))$/,
				  )
				: // if not NT, do not allow leading zero (DDMP-1568)
				  new RegExp(
						/^(([1-9][289][0-9]{2})|([1345689][0-9]{3})|(2[0-8][0-9]{2})|(290[0-9])|(291[0-4])|(7[0-4][0-9]{2})|(7[8-9][0-9]{2}))$/,
				  );

		return regex.test(fieldValue) ? undefined : message;
	},

	'postcode-required': ({ formValues, options, message }: IProps) => {
		// override the empty string with default error message
		message = message || CONTENT.VALIDATORS.DEFAULT.REQUIRED;

		const targetValue = getIn(formValues, options.targetField);
		const currPostalValue = getIn(formValues, options.fieldName);

		if (targetValue !== AUSTRALIA_COUNTRY_CODE) {
			return undefined;
		}

		if (targetValue === AUSTRALIA_COUNTRY_CODE && !currPostalValue.trim()) {
			return (
				options.postcodeErrorMessages?.required ??
				options.postcodeErrorMessages?.au ??
				message
			);
		}

		if (!currPostalValue.length) {
			return message;
		}

		return undefined;
	},

	'security-code': ({ fieldValue, message }: IProps) => {
		message = message || CONTENT.VALIDATORS.DEFAULT.SECURITY_CODE;
		const regex = new RegExp(/^[0-9]{6}$/);

		if (!fieldValue) {
			return undefined;
		}

		return regex.test(fieldValue) ? undefined : message;
	},

	'standalonePostcodeVerify': ({ fieldValue, message }: IProps) => {
		message = message || CONTENT.VALIDATORS.DEFAULT.INTL_POSTCODE;
		const regex = new RegExp(/^[a-z0-9]{0,20}$/i);

		if (!fieldValue) {
			return undefined;
		}

		return regex.test(fieldValue) ? undefined : message;
	},

	'captcha': ({ message, options }: IProps) => {
		message = message || CONTENT.VALIDATORS.DEFAULT.CAPTCHA;

		if (!options.hasToken) {
			return message;
		}

		return undefined;
	},
	'bank-name': ({ fieldValue, message }: IProps) => {
		message = message || CONTENT.VALIDATORS.DEFAULT.BANK_NAME;
		// NOTE: refer to https://australiansuper.atlassian.net/wiki/spaces/MemberPortalV5/pages/46406580/GL04+-+Common+CMS+Definitions+and+Form+Fields
		const regex = new RegExp(
			/^(?! )(?:(?! {2,}|[_\.\\\/()\[\]\{\}\<\>,`#@%\*\^+"¼½\$|]).)*(?:(?![_\.\\\/()\[\]\{\}\<\>,`#@%\*\^+"¼½\$| ]).)$/,
		);

		if (!fieldValue) {
			return undefined;
		}

		return regex.test(fieldValue) ? undefined : message;
	},
	'transferAmount': ({ fieldValue, formValues, message, options }: IProps) => {
		message = message || CONTENT.VALIDATORS.DEFAULT.AMOUNT_EXCEEDS_BALANCE;
		const availableBalance = parseFloat(
			options.availableBalanceField
				? getIn(formValues, options.availableBalanceField)
				: options.availableBalance,
		);
		const amountToTransfer = parseFloat(fieldValue);

		if (!isNaN(availableBalance) && !isNaN(amountToTransfer)) {
			return amountToTransfer > availableBalance ? message : undefined;
		}

		return undefined;
	},
	'fundRollover': ({ fieldValue, message }: IProps) => {
		const eRolloverFundValues = Object.values(fieldValue);

		if (!eRolloverFundValues.filter(Boolean).length) {
			return message;
		}

		return undefined;
	},

	'lpServiceId': ({ fieldValue, message }: IProps) => {
		message = message || CONTENT.VALIDATORS.DEFAULT.LP_SERVICE_ID;

		const regex = new RegExp(/^\d{6,12}$/);
		const isValid = regex.test(fieldValue);

		if (!fieldValue) {
			return undefined;
		}

		if (!isValid) {
			return message;
		}

		return undefined;
	},
	'lpServiceIdRejected': ({ message, options }: IProps) => {
		if (options) {
			return message || CONTENT.VALIDATORS.DEFAULT.LP_SERVICE_ID;
		}

		return undefined;
	},
	'esaDropdown': ({ fieldValue, message, options }: IProps) => {
		const { smsfRollovers, esaPerFund } = options;

		// Filter true values only on fieldValues since they are the selected ones
		const selectedFieldValuesArray = Object.keys(fieldValue).filter(key => {
			return fieldValue[key];
		});
		// Separate SMSF titles into array for easier mapping
		const smsfRolloversArray = smsfRollovers.map((fund: any) => fund?.fundId);
		// Check if the selectedFieldValuesArray are inside smsfRollovers fundId array
		const selectedSMSFArray = selectedFieldValuesArray.filter(elem =>
			smsfRolloversArray.includes(elem),
		);

		if (!isEqual(Object.keys(esaPerFund)?.sort(), selectedSMSFArray.sort())) {
			return message;
		}

		return undefined;
	},

	'investmentOrder': ({ fieldValue, formValues, message, options }: IProps) => {
		const fieldIntVal = parseInt(fieldValue);
		const optionIds = options.investmentsOption.map(
			(v: InvestmentOption | InvestmentOptionPaymentMethod) => {
				const investment: string =
					options?.initialStep === -1
						? (v as InvestmentOption)?.fields?.investmentOptionCode?.value
						: (v as InvestmentOptionPaymentMethod)?.investment_id;

				return `${options.prefix}-${kebabCase(investment)}`;
			},
		);

		// Check if the field value is in the target sequence array
		const targetSequence = Array.from(
			{ length: optionIds.length },
			(_, i) => i + 1,
		);

		if (fieldIntVal > optionIds?.length) {
			return message;
		}

		if (!targetSequence.includes(fieldIntVal)) {
			return message;
		}

		// Filter out the option values first
		const optionVals = Object.keys(formValues)
			.filter(k => optionIds.includes(k) && formValues[k])
			.map(key => parseInt(formValues[key] || '0', 10))
			.sort();

		const curValDupes = optionVals.filter(f => f === fieldIntVal);
		if (curValDupes.length > 1) {
			return message;
		}

		return undefined;
	},

	'investmentAllocation': ({ fieldValue, message }: IProps) => {
		const allocationPercentage = Number(fieldValue);
		if (inRange(allocationPercentage, 0, 100) || allocationPercentage === 100) {
			return undefined;
		} else {
			return message;
		}
	},

	'financialHardshipPaymentValue': ({ fieldValue, message }: IProps) => {
		// TODO: Confirm the correct message for this one
		// See: Associated User Stories #9 Scenario 2 & 3
		message = message || 'Invalid format';
		// Note: https://australiansuper.atlassian.net/wiki/spaces/MemberPortalV5/pages/46400453/T19.E03.P01+-+Financial+Hardship+Claim+Form
		const regex = new RegExp(/^(?=(?:\d\.?){0,9}$)\d+(?:\.\d{1,2})?$/gm);

		if (!fieldValue) {
			return undefined;
		}

		return regex.test(fieldValue) ? undefined : message;
	},

	'withdrawalsEligibilityCheckboxes': ({
		fieldValue,
		message,
		options,
	}: IProps) => {
		message = message || CONTENT.VALIDATORS.DEFAULT.REQUIRED;

		const { optionsTotalLength } = options;

		if (!fieldValue) {
			return undefined;
		}

		return Array.isArray(fieldValue) && fieldValue?.length < optionsTotalLength
			? message
			: undefined;
	},

	/**
	 * @description Validation if the user's withdrawal amount is not exceeding the selected investment options.
	 * @param options - Should have an object investmentOptionProps of interface IInvestmentOptionValidatorProps
	 * @returns - Error message (ER33) or undefined
	 */
	'maxWithdrawalAmountForInvestmentOptions': ({
		fieldValue,
		formValues,
		message,
		options,
	}: IProps) => {
		message =
			message || CONTENT.VALIDATORS.WITHDRAWALS.INSUFFICIENT_FUNDS_AVAILABLE;

		const maxWithdrawalAmountForInvestmentOptionsProps: IMaxWithdrawalAmountForInvestmentOptionsProps =
			options?.maxWithdrawalAmountForInvestmentOptionsProps;
		const {
			withdrawalAmountField,
			investmentOptions,
		} = maxWithdrawalAmountForInvestmentOptionsProps;

		const withdrawalAmount = formValues?.[withdrawalAmountField];

		const amountToTransfer = parseFloat(withdrawalAmount);
		const selectedOptions:
			| TInvestmentOption[]
			| [] = investmentOptions?.filter((option: TInvestmentOption) =>
			fieldValue?.includes(option?.id),
		);

		const maxSelectedAmount = selectedOptions?.reduce(
			(sum, currentValue) => sum + currentValue?.amount,
			0,
		);

		// This condition is to satisfy the investment options
		// requirement of https://australiansuper.atlassian.net/browse/DDMP-269
		if (selectedOptions.length === 0) {
			// Return NO error message if there was no selected investment options
			return undefined;
		} else if (!isNaN(amountToTransfer) && !isNaN(maxSelectedAmount)) {
			return amountToTransfer > maxSelectedAmount ? message : undefined;
		}

		return undefined;
	},

	// @note This is a temporary fix to Debt's required field in FHA.
	// Long term fix should be included in the 'required' validator type.
	'requiredDebt': ({ fieldValue, message }: IProps) => {
		// override the empty string with default error message
		message = message || CONTENT.VALIDATORS.WITHDRAWALS.REQUIRED_DEBT;

		// ignore the falsy value if the value is boolean
		if (!fieldValue && typeof fieldValue !== 'boolean') {
			return message;
		}

		// check for empty string
		if (
			(typeof fieldValue === 'string' || fieldValue instanceof String) &&
			!fieldValue.trim()
		) {
			return message;
		}

		// check for empty array
		if (Array.isArray(fieldValue) && !fieldValue.length) {
			return message;
		}

		return undefined;
	},

	'username': ({ fieldValue, message }: IProps) => {
		message = message || CONTENT.VALIDATORS.DEFAULT.INVALID_USERNAME;
		const regex = new RegExp(/^[a-zA-Z0-9-_.+@]*$/);

		if (!fieldValue) {
			return undefined;
		}

		if (!regex.test(fieldValue)) {
			return message;
		}

		return undefined;
	},

	'wholeNumber': ({ fieldValue, message }: IProps) => {
		// Note: ER99
		message = message || CONTENT.VALIDATORS.WITHDRAWALS.WHOLE_NUMBER;

		// Note: Regex to allow only digits and a decimal point character
		const regex = new RegExp(/^\d+\.?$/);

		if (!fieldValue) {
			return undefined;
		}

		if (!regex.test(fieldValue)) {
			return message;
		}

		return undefined;
	},

	/**
	 * @note Initial validation for ABN. No requirement so far.
	 */
	'abn': ({ fieldValue, message }: IProps) => {
		// Note: Create default content for ABN
		message = message || 'Please enter a valid ABN.';

		// const regex = new RegExp(/^\d+$/);
		const regex = new RegExp(/^[0-9 ]*$/);
		if (!fieldValue) {
			return undefined;
		}

		return regex.test(fieldValue) ? undefined : message;
	},

	/**
	 * @note Not required but is added as an additional check
	 * to prevent submission on fund lookup's initial state since
	 * Fund name, Fund ABN & USI fields are hidden at the start
	 */
	'fundLookupRequired': ({
		fieldValue,
		message,
		formValues,
		options,
	}: IProps) => {
		message = message || CONTENT.VALIDATORS.DEFAULT.REQUIRED;

		/**
		 * Note: Since Fund name/ABN field & Spin/USI share the same result
		 * if one has a value already (from dropdown select) then the other should be
		 * "passed" even if it has 'undefined' value.
		 */
		if (!!formValues?.[options?.otherFieldPath]) {
			return undefined;
		}

		/**
		 * Note: Validations below are the same as 'required' validator.
		 */

		// ignore the falsy value if the value is boolean
		if (!fieldValue && typeof fieldValue !== 'boolean') {
			return message;
		}

		// check for empty string
		if (
			(typeof fieldValue === 'string' || fieldValue instanceof String) &&
			!fieldValue.trim()
		) {
			return message;
		}

		// check for empty array
		if (Array.isArray(fieldValue) && !fieldValue.length) {
			return message;
		}

		return undefined;
	},

	// TODO: This will be used on the component being develop
	// for this use case:
	// https://www.figma.com/proto/2BEqt5hcan4EMDuzvqPQEf/Showcase?page-id=903%3A53924&node-id=908%3A62407&viewport=334%2C37%2C0.02&scaling=scale-down&starting-point-node-id=908%3A62170&show-proto-sidebar=1
	'ageEligibilityCheckboxes': ({ fieldValue, message }: IProps) => {
		message = message || CONTENT.VALIDATORS.DEFAULT.REQUIRED;
		if (!fieldValue) {
			return undefined;
		}

		return Array.isArray(fieldValue) && fieldValue?.length > 0
			? undefined
			: message;
	},

	/*** Validation to make sure that the claimed amount should not be greater than or equal to the maximum amount value */
	'maxEqualGreaterThanAmount': ({ fieldValue, message, options }: IProps) => {
		const maximumAmount = options?.maximumAmount
			? parseFloat(options?.maximumAmount)
			: 10000;
		const amountToTransfer = parseFloat(fieldValue);

		if (!isNaN(amountToTransfer)) {
			return amountToTransfer >= maximumAmount ? message : undefined;
		}

		return undefined;
	},

	// Exclude decimal places maxLength input
	'maxLengthExcludeDecimalValue': ({
		fieldValue,
		message,
		options,
	}: IProps) => {
		message = message || CONTENT.VALIDATORS.DEFAULT.MAX_LENGTH;

		if (!fieldValue) {
			return undefined;
		}

		if (fieldValue.indexOf('.') === -1) {
			return fieldValue?.length > options?.max ? message : undefined;
		} else {
			const splitValue = fieldValue?.split('.');

			return splitValue[0].length > options?.max ? message : undefined;
		}
	},

	/*** @note This is a Direct Debit specific validation that limits the users to enter
	 * an amount greater than $330,000 or any amount specified in the future as per the requirements stated in
	 * https://australiansuper.atlassian.net/browse/MI0031-2028
	 * Once the user enters an amount greater than $330K, an error will bre thrown in the UI.
	 */
	'maxDirectDebitAmount': ({ fieldValue, message, options }: IProps) => {
		message = message || CONTENT.VALIDATORS.DEFAULT.MAX_LENGTH;
		if (!fieldValue) {
			return undefined;
		}

		if (Number(fieldValue) > Number(options?.maxAmount)) {
			return message;
		}

		return undefined;
	},
	/**
	 * This change has been requested by AustralianSuper Operations as a fraud mitigant.
	 *
	 * If the member updates the account name (removes the pre-populated account name),
	 * the new value entered in ‘Account name' input field must contain the member’s
	 * (in any order):
	 * - Surname, and
	 * - First initial of the first name
	 *
	 * Additional rules:
	 * - First name+“ “ AND Surname
	 * - First name Initial+" " AND Surname
	 * - Surname+“ “ AND First name
	 * - Surname+“ “ AND First name Initial
	 *
	 * Note: initial is isolated as a single word
	 *
	 * Refer: https://australiansuper.atlassian.net/browse/MI0031-2056
	 */
	'accountName': ({ fieldValue, message, options }: IProps) => {
		message = message || CONTENT.VALIDATORS.DEFAULT.ACCOUNT_NAME;

		const inputValue = fieldValue?.toLowerCase().trim() ?? '';
		const fname = options?.firstName?.toLowerCase().trim() ?? '';
		const lname = options?.lastName?.toLowerCase().trim() ?? '';
		const firstNameInitial = fname.slice(0, 1);
		let nameWithoutLastName = '';
		let isValidLastName = false;

		// return if no change in account name field
		if (inputValue === fname.concat(' ', lname)) {
			return;
		}

		if (!fieldValue) {
			return undefined;
		}

		const fnameCounter = fname.split(' ').length;
		const hasMoreThanOneFname = fnameCounter > 1;

		// remove last name in the input value string
		if (inputValue.includes(lname)) {
			const escapedLastName = lname.replace(' ', '-');
			const escapedInputName = inputValue.replace(lname, escapedLastName);

			if (
				escapedInputName
					.split(' ')
					.some((lastName: string) => lastName === escapedLastName)
			) {
				isValidLastName = true;
			}

			nameWithoutLastName = escapedInputName.replace(escapedLastName, '');
		}

		// if more than 1 fname, get the first of the 2 then check if match to the account name field
		const accountNameChecker = () => {
			const firstName = hasMoreThanOneFname ? fname.split(' ')[0] : fname;
			const isValid = nameWithoutLastName
				.split(' ')
				.filter(fnameFilter => fnameFilter !== '')
				.some((someFname: string) => {
					if (someFname === firstName || someFname === firstNameInitial) {
						return true;
					} else return false;
				});

			return isValid;
		};

		const isAccountNameValid = isValidLastName && accountNameChecker();

		return isAccountNameValid ? undefined : message;
	},
};
